<script setup lang="ts">
interface Props {
  url: Maybe<string>
  isAutoplay?: boolean
}

const props = defineProps<Props>()
const { onLoaded } = useScriptYouTubePlayer({})

const player = ref(null)

const isLoaded = ref<boolean>(false)
const isPlaying = ref<boolean>(false)
const youtubeVideo = ref()
const play = async () => {
  await youtubeVideo.value.player.playVideo()
}
const stateChange = async (event: YT.OnStateChangeEvent) => {
  isPlaying.value = event.data === 1
}

const youtubeEmbedId = computed<Maybe<string>>(() => {
  if (!props.url) {
    return null
  }

  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  const match = props.url.match(regExp)

  return match && match[7].length == 11 ? match[7] : null
})

onLoaded(async ({ YT }) => {
  const YouTube = await YT
  await new Promise<void>((resolve) => {
    if (typeof YT.Player === 'undefined')
      YouTube.ready(resolve)
    else
      resolve()
  })
  player.value = new YT.Player(youtubeVideo.value, {
    videoId: youtubeEmbedId.value,
  })
})
</script>

<template>
  <ScriptYouTubePlayer
    v-if="youtubeEmbedId"
    ref="youtubeVideo"
    :video-id="youtubeEmbedId"
    class="yt-video"
    :player-vars="{ autoplay: isAutoplay? 1 : 0 }"
    @ready="isLoaded = true"
    @state-change="stateChange"
  >
    <template
      v-if="!isAutoplay"
      #awaitingLoad
    >
      <div class="yt-video__thumb">
        <UIButton
          v-if="!isPlaying"
          is-label-hidden
          prepended-icon="mdi:play"
          variant="secondary"
          class="yt-video__button"
          @click="play"
        >
          Play Video
        </UIButton>
      </div>
    </template>
  </ScriptYouTubePlayer>
</template>

<style scoped lang="scss">
.yt-video {
  @apply w-full flex flex-col items-start gap-[20px];

  &__thumb {
    @apply absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-[48px] w-[68px];
  }

  &__image {
    @apply absolute left-0 top-0 w-full h-full object-cover -z-[1] transition-transform duration-500;
  }

  &__thumb:hover &__image {
    @apply scale-110;
  }

  &__button {
    @apply absolute left-[16px] bottom-[16px] px-[5px];
    @apply md:px-[11px];
    @apply max-md:h-[24px];

    &:deep(.button__icon) {
      @apply max-md:w-[12px] max-md:h-[12px];
    }
  }

  &__meta {
    @apply flex flex-col gap-[10px] items-start;
  }

  &__date {
    @apply text-[14px] font-medium text-white/60 block leading-[1];
  }

  &__title {
    @apply text-white max-lg:text-[18px];
  }

  &__title-link {
    @apply underline underline-offset-2 decoration-transparent transition-colors duration-300;
    @apply hover:decoration-white;
  }
}
</style>
